import { z } from "zod";
import { schemas } from "../generated/serverClient";

export const DrivingLicenseClassEnum = schemas.DrivingLicenseClassEnum;

export type DrivingLicenseClass = z.infer<typeof DrivingLicenseClassEnum>;

export const CAR_ONLY_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "B",
  "B197",
  "B78",
  "L",
]);

export const CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "B96",
  "BE",
]);

export const MOTORCYCLE_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "A",
  "A1",
  "A2",
  "A80",
  "AM",
  "B196",
  "M",
]);

export const TRUCK_ONLY_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "C",
  "C1",
]);

export const TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> =
  new Set<DrivingLicenseClass>(["C1E", "CE"]);

export const TRUCK_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  ...TRUCK_ONLY_DRIVING_LICENSE_CLASSES,
  ...TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
]);

export const BUS_ONLY_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "D",
  "D1",
]);

export const BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "D1E",
  "DE",
]);

export const BUS_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  ...BUS_ONLY_DRIVING_LICENSE_CLASSES,
  ...BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
]);

export const TRACTOR_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  "L",
  "T",
]);

export const TRAILER_DRIVING_LICENSE_CLASSES: ReadonlySet<DrivingLicenseClass> = new Set<DrivingLicenseClass>([
  ...CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  ...TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
  ...BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES,
]);

export function isForCarOnly(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return CAR_ONLY_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForCarWithTrailer(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return CAR_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForCar(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return isForCarOnly(x) || isForCarWithTrailer(x);
}

export function isForMotorcycle(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return MOTORCYCLE_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForTruckOnly(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return TRUCK_ONLY_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForTruckWithTrailer(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return TRUCK_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForTruck(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return isForTruckOnly(x) || isForTruckWithTrailer(x);
}

export function isForBusOnly(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return BUS_ONLY_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForBusWithTrailer(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return BUS_WITH_TRAILER_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isForBus(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return isForBusOnly(x) || isForBusWithTrailer(x);
}

export function isForTractor(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return TRACTOR_DRIVING_LICENSE_CLASSES.has(typeof x === "string" ? x : x.drivingLicenseClass);
}

export function isWithTrailer(x: DrivingLicenseClass | { drivingLicenseClass: DrivingLicenseClass }): boolean {
  return isForCarWithTrailer(x) || isForTruckWithTrailer(x) || isForBusWithTrailer(x);
}

export const DRIVING_LICENSE_CLASSES_CHOICES: { id: DrivingLicenseClass; name: string }[] =
  DrivingLicenseClassEnum.options.map((option) => ({ id: option, name: option }));
